import { useState, useEffect, useCallback } from 'react';
import { useProviderAuth } from '../../contexts/ProviderAuthContext';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Mail, CheckCircle, AlertTriangle, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function EmailVerificationBanner() {
  const { user, token } = useProviderAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  // Check verification status
  const checkStatus = useCallback(async () => {
    if (!token) return;
    
    try {
      const res = await fetch(`${API_URL}/api/provider/auth/verification-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setStatus(data);
    } catch (err) {
      console.error('Error checking verification status:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  // Cooldown countdown
  useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldown]);

  // Send verification email
  const handleSendVerification = async () => {
    setSending(true);
    
    try {
      const res = await fetch(`${API_URL}/api/provider/auth/send-verification`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      
      if (data.success) {
        toast.success(data.message);
        if (data.wait_seconds) {
          setCooldown(data.wait_seconds);
        }
      } else {
        toast.error(data.message);
        if (data.wait_seconds) {
          setCooldown(data.wait_seconds);
        }
      }
    } catch (err) {
      toast.error('Errore durante l\'invio. Riprova più tardi.');
    } finally {
      setSending(false);
    }
  };

  // Don't show if loading or already verified
  if (loading) return null;
  if (status?.verified) return null;

  return (
    <Card className="bg-yellow-50 border-yellow-200 mb-6">
      <CardContent className="flex items-center justify-between py-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-yellow-100 rounded-full">
            <AlertTriangle className="w-5 h-5 text-yellow-600" />
          </div>
          <div>
            <p className="font-medium text-yellow-900">
              Verifica il tuo indirizzo email
            </p>
            <p className="text-sm text-yellow-700">
              Controlla {user?.email} per il link di conferma
            </p>
          </div>
        </div>
        
        <Button
          variant="outline"
          size="sm"
          onClick={handleSendVerification}
          disabled={sending || cooldown > 0}
          className="border-yellow-400 hover:bg-yellow-100"
        >
          {sending ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Invio...
            </>
          ) : cooldown > 0 ? (
            <>Riprova tra {cooldown}s</>
          ) : (
            <>
              <Send className="w-4 h-4 mr-2" />
              Reinvia Email
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}

export function EmailVerificationStatus({ compact = false }) {
  const { token } = useProviderAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkStatus = async () => {
      if (!token) return;
      
      try {
        const res = await fetch(`${API_URL}/api/provider/auth/verification-status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        setStatus(data);
      } catch (err) {
        console.error('Error checking verification status:', err);
      } finally {
        setLoading(false);
      }
    };

    checkStatus();
  }, [token]);

  if (loading) return null;

  if (compact) {
    return status?.verified ? (
      <div className="flex items-center gap-1 text-green-600 text-sm">
        <CheckCircle className="w-4 h-4" />
        <span>Verificato</span>
      </div>
    ) : (
      <div className="flex items-center gap-1 text-yellow-600 text-sm">
        <AlertTriangle className="w-4 h-4" />
        <span>Non verificato</span>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
      status?.verified 
        ? 'bg-green-50 text-green-700 border border-green-200'
        : 'bg-yellow-50 text-yellow-700 border border-yellow-200'
    }`}>
      {status?.verified ? (
        <>
          <CheckCircle className="w-5 h-5" />
          <div>
            <p className="font-medium">Email Verificata</p>
            <p className="text-sm opacity-80">{status.email}</p>
          </div>
        </>
      ) : (
        <>
          <Mail className="w-5 h-5" />
          <div>
            <p className="font-medium">Email Non Verificata</p>
            <p className="text-sm opacity-80">Controlla la tua casella</p>
          </div>
        </>
      )}
    </div>
  );
}
